import * as z from 'zod';
import { contractSuretyBondTypeSchema, suretyBondRenewalMethodSchema, ContractSuretyType, SuretyType, dateTimeSchema } from '../../../../../enums.mjs';
import '../../../../../types/actor/actor.mjs';
import '../../../../../types/address/address.mjs';
import '../../../../../types/credit-report/company-credit-report.mjs';
import '../../../../../types/credit-report/individual-credit-report.mjs';
import '../../../../../types/currency/currency.mjs';
import '../../../../../types/entityRelation/entity-relation.mjs';
import '../../../../../types/financial-formula/adjustable-value.mjs';
import '../../../../../types/financial-formula/adjustable-values-update.mjs';
import '../../../../../types/financial-formula/adjustment.mjs';
import '../../../../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../../../../types/is-encrypted.mjs';
import '../../../../../types/naics-codes/naics-codes.mjs';
import { timePeriodSchema, fileId } from '../../../../../types/opaque.mjs';
import '../../../../../types/surety/account/requested-limits.mjs';
import '../../../../../types/surety/account/surety-account.types.mjs';
import '../../../../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../../../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../../../../types/surety/application-extraction/application-review.mjs';
import '../../../../../types/surety/bond/surety-bond.types.mjs';
import '../../../../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../../../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../../../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../../../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../../../../types/surety/contract/contract-extraction-output.mjs';
import '../../../../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../../../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../../../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../../../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../../../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../../../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../../../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../../../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../../../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../../../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../../../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../../../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../../../../types/surety/quote/surety-quote.types.mjs';
import '../../../../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../../../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../../../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../../../../types/user/user.types.mjs';
import '../../../../../types/utils.mjs';
import { principalSignerSchema } from '../../../../../types/surety/bond-form/issued/contract/issued-contract-surety-bond-form.types.mixins.mjs';
import { Obligee } from '../../../../../types/surety/bond/obligee.types.mjs';

var Approve;
(function (Approve) {
    (function (Request) {
        Request.schema = z.object({
            suretyType: z.literal(SuretyType.contract),
            contractSuretyType: z.literal(ContractSuretyType.final),
            bondFormType: contractSuretyBondTypeSchema,
            cancellable: z.boolean(),
            continuous: z.boolean(),
            renewable: z.boolean(),
            renewalMethod: suretyBondRenewalMethodSchema.nullish(),
            numberOfPeriods: z.number().nullish(),
            renewOnDay: z
                .object({
                month: z.number().min(1).max(12),
                day: z.number(),
            })
                .nullish(),
            renewThroughYear: z.number().nullish(),
            periodLength: timePeriodSchema.nullish(),
        });
    })(Approve.Request || (Approve.Request = {}));
})(Approve || (Approve = {}));
var Upload;
(function (Upload) {
    (function (AgentIssue) {
        (function (Request) {
            Request.schema = z.object({
                suretyType: z.literal(SuretyType.contract),
                contractSuretyType: z.literal(ContractSuretyType.final),
                cancellable: z.boolean(),
                continuous: z.boolean(),
                bondForm: z.object({
                    fileId: fileId,
                    data: z.object({
                        bondNumber: z.string(),
                        effectiveDate: dateTimeSchema,
                        contractDate: dateTimeSchema,
                        contractDescription: z.string(),
                        obligees: Obligee.schema.array(),
                        principalSigner: principalSignerSchema.nullish(),
                    }),
                }),
            });
        })(AgentIssue.Request || (AgentIssue.Request = {}));
    })(Upload.AgentIssue || (Upload.AgentIssue = {}));
    (function (PhysicalIssue) {
        (function (Request) {
            Request.schema = z.object({
                suretyType: z.literal(SuretyType.contract),
                contractSuretyType: z.union([
                    z.literal(ContractSuretyType.final),
                    z.literal(ContractSuretyType.bid_to_final),
                ]),
                bondFormType: contractSuretyBondTypeSchema,
                attributes: z.object({
                    cancellable: z.boolean(),
                    continuous: z.boolean(),
                    renewable: z.boolean(),
                    renewalMethod: suretyBondRenewalMethodSchema.nullish(),
                    numberOfPeriods: z.number().nullish(),
                    renewOnDay: z
                        .object({
                        month: z.number().min(1).max(12),
                        day: z.number(),
                    })
                        .nullish(),
                    renewThroughYear: z.number().nullish(),
                    periodLength: timePeriodSchema.nullish(),
                }),
                bondForm: z.object({
                    fileId: fileId,
                    data: z.object({
                        bondNumber: z.string(),
                        effectiveDate: dateTimeSchema,
                        contractDate: dateTimeSchema,
                        contractDescription: z.string(),
                        obligees: z.array(Obligee.schema),
                        principalSigner: z.preprocess((val) => (typeof val === "object" && Object.keys(val).length === 0 ? undefined : val), principalSignerSchema.nullish()),
                    }),
                }),
            });
        })(PhysicalIssue.Request || (PhysicalIssue.Request = {}));
    })(Upload.PhysicalIssue || (Upload.PhysicalIssue = {}));
})(Upload || (Upload = {}));

export { Approve, Upload };
